import { useCallback, useEffect, useMemo, useState } from 'react';
import { notificationAPI } from '../services/api';
import { useNotifications } from '../context/NotificationContext';
import {
  ActivityItem,
  feedItemToActivity,
  notificationSubjectKey,
  receiptToActivity,
  toActivity,
} from '../utils/notificationMapper';
import { playEmergencyAlert } from '../utils/emergencyAlert';

// Emergencies already sounded this app session (survives hook remounts).
const alertedEmergencies = new Set<string>();

// Three months, matching what the server will return. It was five days, which
// silently hid most of a term's notifications even though the feed had them.
const MAX_NOTIFICATION_AGE_MS = 92 * 24 * 60 * 60 * 1000;

/**
 * The parent's notification list: the PERSISTED feed from
 * GET /Notifications/Parent merged with live SignalR messages.
 *
 * Communication-engine messages arrive on both paths (the feed row is written
 * before the SignalR poke), so live 'Communication' events are not rendered
 * directly — they trigger a feed refresh instead. Everything else (canteen
 * orders, legacy broadcasts) only exists live and is kept as before.
 */
export function useParentFeed() {
  const { notifications, removeNotification } = useNotifications();
  const [feedItems, setFeedItems] = useState<ActivityItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [activeEmergency, setActiveEmergency] = useState<ActivityItem | null>(null);
  // Email and push receipts. Kept apart from feedItems because they carry no read
  // state and must never be marked read or acknowledged.
  const [receipts, setReceipts] = useState<ActivityItem[]>([]);

  const refresh = useCallback(async () => {
    try {
      const response = await notificationAPI.getNotifications(1, 50);
      const items = response.data?.items ?? [];
      const allMapped: ActivityItem[] = items.map(feedItemToActivity);
      const mapped: ActivityItem[] = allMapped
        // Once seen, a notification leaves this page. The one exception is an
        // item still awaiting acknowledgement — hiding it before the parent
        // acknowledges would make the [Acknowledge] action unreachable.
        .filter((item: ActivityItem) =>
          item.isNew || (item.requiresAcknowledgement && !item.acknowledgedAt));
      setFeedItems(mapped);

      // Keyed on EVERY feed row, not just the unread ones kept above: a receipt for
      // a message the parent has already read is just as duplicative.
      const feedSubjects = new Set(
        allMapped.map(item => notificationSubjectKey(item.name)),
      );
      setUnreadCount(response.data?.unreadCount ?? 0);

      // Email and push receipts, fetched separately and never allowed to break the
      // feed: this screen worked without them before, and a history request that
      // fails must not cost the parent the circulars they can act on.
      //
      // Rows the feed above already carries are dropped: the feed row has the child's
      // name, the body and the read state, where the receipt only says "Email", so
      // showing both rendered every message twice — once usefully, once not. This was
      // always the intent; the filter that enforces it was missing, so the list showed
      // a duplicate, content-free row for every notification the school sent.
      //
      // A FAILED delivery is kept even when the feed has the message, because
      // "this did not arrive" is the one thing the feed row cannot tell the parent.
      try {
        const history = await notificationAPI.getMyHistory(1, 100);
        const receiptRows = (history.data?.items ?? [])
          .filter((r: any) => r.channel === 'EMAIL' || r.channel === 'PUSH')
          .map(receiptToActivity)
          .filter((r: ActivityItem) =>
            r.deliveryFailed || !feedSubjects.has(notificationSubjectKey(r.name)));
        setReceipts(receiptRows);
      } catch {
        // Leave whatever was already loaded rather than blanking the list.
      }

      // An unread, unacknowledged EMERGENCY sounds the siren once per item and
      // raises the banner. Both delivery paths land here: the feed poll and
      // the SignalR poke (which triggers this refresh).
      const emergency = mapped.find(item =>
        item.category === 'EMERGENCY' && item.isNew && !item.acknowledgedAt);
      if (emergency?.recipientId && !alertedEmergencies.has(emergency.recipientId)) {
        alertedEmergencies.add(emergency.recipientId);
        playEmergencyAlert();
      }
      setActiveEmergency(emergency ?? null);
    } catch {
      // The feed being briefly unreachable must not blank the screen;
      // whatever was last loaded stays.
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // A live engine message means the feed has a new row — refetch it.
  const communicationCount = notifications.filter(n => n.eventType === 'Communication').length;
  useEffect(() => {
    if (communicationCount > 0) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communicationCount]);

  const activities = useMemo(() => {
    const live = notifications
      .filter(n => n.eventType !== 'Communication')
      .map((msg, index) => toActivity(msg, index < 5));

    const cutoff = Date.now() - MAX_NOTIFICATION_AGE_MS;

    return [...feedItems, ...receipts, ...live]
      // A display trim only — anything older than the server's own three-month
      // window will not have been returned in the first place.
      .filter(item => new Date(item.createdAt).getTime() >= cutoff)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [feedItems, receipts, notifications]);

  const markRead = useCallback(async (item: ActivityItem) => {
    if (!item.recipientId) {
      // A live SignalR message has no server row to mark — seen means it just
      // leaves the local list (and its AsyncStorage copy).
      removeNotification(item.id);
      return;
    }
    try {
      await notificationAPI.markAsRead(item.recipientId);
      setUnreadCount(c => Math.max(0, c - 1));
      // Seen means gone from this page — except an item still awaiting
      // acknowledgement, which stays until the parent acknowledges it.
      setFeedItems(list => list
        .map(f => f.recipientId === item.recipientId ? { ...f, isNew: false } : f)
        .filter(f => f.isNew || (f.requiresAcknowledgement && !f.acknowledgedAt)));
    } catch {
      // Read-marking is best-effort; the next refresh reconciles.
    }
  }, [removeNotification]);

  const acknowledge = useCallback(async (item: ActivityItem, device?: string) => {
    if (!item.recipientId) return;
    try {
      await notificationAPI.acknowledge(item.recipientId, device);
      await refresh();
    } catch {
      // Surfaced by the unchanged "acknowledge" state on refresh.
    }
  }, [refresh]);

  return { activities, unreadCount, isLoading, refresh, markRead, acknowledge, activeEmergency };
}
